import { useState, useEffect, useRef } from "react";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import SharedService from "@/services/shared-service";
import { ScrollShadow } from "@nextui-org/react";
import Link from "next/link";

function Taize({ data, autoPlayInterval = 5000 }) {
    const [selectedId, setSelectedId] = useState();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const currentEvent = data[currentSlide];
    const sectionRef = useRef(null);
    const containerRef = useRef(null);
    const itemRefs = useRef({});

    useEffect(() => {
        if (!isHovered && data.length > 0) {
            const timer = setInterval(() => {
                handleNextSlide();
            }, autoPlayInterval);
            return () => clearInterval(timer);
        }
    }, [isHovered, currentSlide, data.length]);

    useEffect(() => {
        if (currentEvent) {
            setSelectedId(currentEvent?.id);
        }
    }, [currentEvent]);

    useEffect(() => {
        if (!selectedId) return;

        const container = containerRef.current;
        const item = itemRefs.current[selectedId];

        if (container && item) {
            container.scrollTo({
                top: item.offsetTop - container.offsetTop - 25,
                behavior: "smooth",
            });
        }
    }, [selectedId]);

    const handleNextSlide = () => {
        setCurrentSlide((prev) => (prev === data.length - 1 ? 0 : prev + 1));
    };

    const handlePrevSlide = () => {
        setCurrentSlide((prev) => (prev === 0 ? data.length - 1 : prev - 1));
    };

    return (
        <section
            ref={sectionRef}
            className="md:grid md:grid-cols-12 md:items-stretch gap-4 pt-5"
        >
            <div className="md:col-span-9 w-full h-full">
                <div
                    className="relative bg-gradient-to-r from-red-600 via-red-500 to-orange-400 rounded-[5px] shadow-md overflow-hidden h-full"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {/* Background Image Overlay */}
                    {currentEvent?.photo && (
                        <div
                            className="absolute inset-0 bg-cover bg-center opacity-20"
                            style={{
                                backgroundImage: `url(${SharedService.getOptimizeImage(currentEvent.photo, "hd")})`,
                            }}
                        />
                    )}

                    {/* Navigation Arrows */}
                    <button
                        onClick={handlePrevSlide}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-all"
                        aria-label="Previous event"
                    >
                        <BiChevronLeft className="w-8 h-8 text-white" />
                    </button>

                    <button
                        onClick={handleNextSlide}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-all"
                        aria-label="Next event"
                    >
                        <BiChevronRight className="w-8 h-8 text-white" />
                    </button>

                    {/* Content */}
                    <div className="relative z-10 flex flex-col justify-center items-center h-full px-8 py-12 text-white text-center">
                        {/* Category Badge */}
                        {currentEvent?.categoryName && (
                            <div className="bg-purple-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                                {currentEvent.categoryName}
                            </div>
                        )}

                        {/* Author/Organization */}
                        {currentEvent?.authorName && (
                            <p className="text-lg font-medium mb-4 italic">
                                {currentEvent.authorName}
                            </p>
                        )}

                        {/* Main Title */}
                        <a
                            href={SharedService.getPostPath(currentEvent)}
                            className="block hover:opacity-90 transition-opacity"
                        >
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight max-w-4xl drop-shadow-lg">
                                {currentEvent?.quotation || currentEvent?.title}
                            </h2>

                            {/* Description */}
                            {currentEvent?.metaDescription && (
                                <div
                                    className="text-lg md:text-xl mb-6 max-w-3xl mx-auto line-clamp-3 leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: currentEvent.metaDescription,
                                    }}
                                />
                            )}
                        </a>

                        {/* Date/Time Badge */}
                        {currentEvent?.publishedDate && (
                            <div className="bg-white/20 backdrop-blur-sm text-white px-6 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                                <span>📅</span>
                                <span className="uppercase">
                                    {SharedService.formatISODate(
                                        currentEvent.publishedDate,
                                        "DD.MM.YYYY",
                                    )}
                                </span>
                            </div>
                        )}

                        {/* Location */}
                        {currentEvent?.quotation && currentEvent?.title && (
                            <p className="text-base mt-4 opacity-90">
                                📍 {currentEvent.title}
                            </p>
                        )}
                    </div>

                    {/* Dots Indicator */}
                    {data.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                            {data.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                                        index === currentSlide
                                            ? "bg-white w-8"
                                            : "bg-white/50 hover:bg-white/75"
                                    }`}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="md:col-span-3 w-full h-full">
                <div className="bg-white rounded-[5px] shadow-md h-full flex flex-col overflow-hidden">
                    <div className="bg-primary p-4 text-center">
                        <h2 className="text-white font-bold text-lg uppercase">
                            TAIZE
                        </h2>
                    </div>

                    <ScrollShadow
                        hideScrollBar
                        className="flex-1 max-h-[400px]"
                        ref={containerRef}
                    >
                        <div className="p-4 space-y-4">
                            {data?.map((item) => {
                                const isSelected = selectedId === item.id;

                                return (
                                    <div
                                        key={item.id}
                                        ref={(el) =>
                                            (itemRefs.current[item.id] = el)
                                        }
                                        className={`${isSelected ? "border-l-3 border-primary" : ""} pl-3`}
                                    >
                                        <Link
                                            className="font-semibold text-black hover:underline block"
                                            href={SharedService.getPostPath(item)}
                                            target="_self"
                                        >
                                            {item.title}
                                        </Link>

                                        {item.metaDescription && (
                                            <p className="text-xs text-gray-600 italic mt-1 line-clamp-3">
                                                {item.metaDescription}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </ScrollShadow>
                </div>
            </div>
        </section>
    );
}

export default Taize;
